// The support console: a staff-only page for helping one user at a time.
//
// The backend decides who may use it, from the caller's verified Google
// sign-in and nothing else (backend/app/support-console-access.go). Nothing in
// this folder grants access: it only shows what the backend chose to answer.
//
// The backend returns proto messages as JSON. Every field is read in
// snake_case and camelCase, and enums as numbers or names.

export interface SupportAccess {
  granted: boolean;
  // The person is support staff, but signed in too long ago.
  reauthRequired: boolean;
  maxSessionHours: number;
}

export interface SupportUserSummary {
  courseCount: number;
  firstName: string;
  id: string;
  isInstructor: boolean;
  lastName: string;
  // The search text is this user's profile email.
  matchedEmail: boolean;
  // The search text is this user's ID.
  matchedId: boolean;
  preferredEmail: string;
}

export interface SupportUserDetail {
  firstName: string;
  id: string;
  institute: string;
  instituteOfficialId: string;
  isGoogle: boolean;
  isInstructor: boolean;
  lastLoginAt: Date | null;
  lastName: string;
  preferredEmail: string;
  preferredUsername: string;
}

// How a course names the user. "unknown" is an answer this page could not
// read; it is shown as such and offers no removal.
export type SupportCourseRole =
  | 'student'
  | 'assistant'
  | 'secondary_instructor'
  | 'primary_instructor'
  | 'unknown';

export type SupportCourseType = 'free' | 'free_and_premium' | 'premium' | 'unknown';

export interface SupportInstructor {
  email: string;
  firstName: string;
  id: string;
  lastName: string;
}

// A calendar date as the course stores it: no time zone, no conversion.
export interface SupportCalendarDate {
  day: number;
  month: number;
  year: number;
}

export interface SupportCourseMembership {
  // On the user's archived list, not their course list.
  archived: boolean;
  assistantCount: number;
  courseId: string;
  // Null when the course's ID does not say when it was made.
  createdAt: Date | null;
  endDate: SupportCalendarDate | null;
  // False when the user's record names a course that has no record.
  exists: boolean;
  // The course's roster lists the user.
  inRoster: boolean;
  instructor: SupportInstructor | null;
  isComplete: boolean;
  isLive: boolean;
  lastUpdatedAt: Date | null;
  name: string;
  role: SupportCourseRole;
  secondaryInstructorCount: number;
  startDate: SupportCalendarDate | null;
  studentCount: number;
  type: SupportCourseType;
}

export interface SupportUserRecord {
  courses: SupportCourseMembership[];
  user: SupportUserDetail;
}

// Which of the three places a removal changed.
export interface SupportRemoval {
  removedFromArchivedList: boolean;
  removedFromCourseList: boolean;
  removedFromRoster: boolean;
}

type RawNumber = number | string | null;

export interface RawSupportConsoleAccessResponse {
  granted?: boolean | null;
  max_session_hours?: RawNumber;
  maxSessionHours?: RawNumber;
  reauth_required?: boolean | null;
  reauthRequired?: boolean | null;
}

export interface RawSupportUserSummary {
  course_count?: RawNumber;
  courseCount?: RawNumber;
  first_name?: string | null;
  firstName?: string | null;
  id?: string | null;
  is_instructor?: boolean | null;
  isInstructor?: boolean | null;
  last_name?: string | null;
  lastName?: string | null;
  matched_email?: boolean | null;
  matched_id?: boolean | null;
  matchedEmail?: boolean | null;
  matchedId?: boolean | null;
  preferred_email?: string | null;
  preferredEmail?: string | null;
}

export interface RawSupportConsoleSearchUsersResponse {
  users?: RawSupportUserSummary[] | null;
}

export interface RawSupportUserDetail {
  first_name?: string | null;
  firstName?: string | null;
  id?: string | null;
  institute?: string | null;
  institute_official_id?: string | null;
  instituteOfficialId?: string | null;
  is_google?: boolean | null;
  is_instructor?: boolean | null;
  isGoogle?: boolean | null;
  isInstructor?: boolean | null;
  last_login_utc_seconds?: RawNumber;
  last_name?: string | null;
  lastLoginUtcSeconds?: RawNumber;
  lastName?: string | null;
  preferred_email?: string | null;
  preferred_username?: string | null;
  preferredEmail?: string | null;
  preferredUsername?: string | null;
}

export interface RawSupportInstructor {
  email?: string | null;
  first_name?: string | null;
  firstName?: string | null;
  id?: string | null;
  last_name?: string | null;
  lastName?: string | null;
}

export interface RawSupportCalendarDate {
  day?: RawNumber;
  month?: RawNumber;
  year?: RawNumber;
}

export interface RawSupportCourseMembership {
  archived?: boolean | null;
  assistant_count?: RawNumber;
  assistantCount?: RawNumber;
  course_id?: string | null;
  courseId?: string | null;
  created_utc_seconds?: RawNumber;
  createdUtcSeconds?: RawNumber;
  end_date?: RawSupportCalendarDate | null;
  endDate?: RawSupportCalendarDate | null;
  exists?: boolean | null;
  in_roster?: boolean | null;
  inRoster?: boolean | null;
  instructor?: RawSupportInstructor | null;
  is_complete?: boolean | null;
  is_live?: boolean | null;
  isComplete?: boolean | null;
  isLive?: boolean | null;
  last_updated_utc_seconds?: RawNumber;
  lastUpdatedUtcSeconds?: RawNumber;
  name?: string | null;
  role?: number | string | null;
  secondary_instructor_count?: RawNumber;
  secondaryInstructorCount?: RawNumber;
  start_date?: RawSupportCalendarDate | null;
  startDate?: RawSupportCalendarDate | null;
  student_count?: RawNumber;
  studentCount?: RawNumber;
  type?: number | string | null;
}

export interface RawSupportConsoleGetUserResponse {
  courses?: RawSupportCourseMembership[] | null;
  user?: RawSupportUserDetail | null;
}

export interface RawSupportConsoleRemoveStudentResponse {
  removed_from_archived_list?: boolean | null;
  removed_from_course_list?: boolean | null;
  removed_from_roster?: boolean | null;
  removedFromArchivedList?: boolean | null;
  removedFromCourseList?: boolean | null;
  removedFromRoster?: boolean | null;
}

function readString(...values: Array<string | null | undefined>): string {
  for (const value of values) {
    if (typeof value === 'string' && value.trim() !== '') {
      return value.trim();
    }
  }

  return '';
}

// Only the boolean true counts. A string, a number or anything else is false:
// a flag this page cannot read is never taken as set.
function readFlag(...values: Array<boolean | null | undefined>): boolean {
  return values.some((value) => value === true);
}

function readNumber(...values: Array<RawNumber | undefined>): number {
  for (const value of values) {
    const parsed =
      typeof value === 'number'
        ? value
        : typeof value === 'string' && value.trim() !== ''
          ? Number(value)
          : Number.NaN;

    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return 0;
}

function readCount(...values: Array<RawNumber | undefined>): number {
  return Math.max(0, Math.trunc(readNumber(...values)));
}

// Seconds since the epoch; 0, missing or unreadable means "not known".
function readInstant(...values: Array<RawNumber | undefined>): Date | null {
  const seconds = readNumber(...values);

  if (seconds <= 0) {
    return null;
  }

  const date = new Date(seconds * 1000);
  return Number.isNaN(date.getTime()) ? null : date;
}

function readCalendarDate(
  raw: RawSupportCalendarDate | null | undefined,
): SupportCalendarDate | null {
  if (!raw) {
    return null;
  }

  const day = readCount(raw.day);
  const month = readCount(raw.month);
  const year = readCount(raw.year);

  if (year < 1 || month < 1 || month > 12 || day < 1 || day > 31) {
    return null;
  }

  return { day, month, year };
}

// SupportCourseMembership.Role in backend/proto/course.proto.
const ROLES_BY_CODE: Record<string, SupportCourseRole> = {
  '1': 'student',
  '2': 'assistant',
  '3': 'secondary_instructor',
  '4': 'primary_instructor',
  ASSISTANT: 'assistant',
  PRIMARY_INSTRUCTOR: 'primary_instructor',
  SECONDARY_INSTRUCTOR: 'secondary_instructor',
  STUDENT: 'student',
};

// Course.Type in backend/proto/course.proto.
const TYPES_BY_CODE: Record<string, SupportCourseType> = {
  '1': 'free',
  '2': 'free_and_premium',
  '3': 'premium',
  FREE: 'free',
  FREE_AND_PREMIUM: 'free_and_premium',
  PREMIUM_LEVEL_1: 'premium',
};

function readCode<T extends string>(
  table: Record<string, T>,
  value: number | string | null | undefined,
  fallback: T,
): T {
  if (typeof value !== 'number' && typeof value !== 'string') {
    return fallback;
  }

  const key = String(value).trim();
  return Object.hasOwn(table, key) ? table[key] : fallback;
}

// Anything but a clear "granted" is not granted, and "sign in again" wins over
// "granted" if an answer ever carried both.
export function normalizeSupportAccess(
  raw: RawSupportConsoleAccessResponse | null | undefined,
): SupportAccess {
  const reauthRequired = readFlag(raw?.reauth_required, raw?.reauthRequired);

  return {
    granted: raw?.granted === true && !reauthRequired,
    maxSessionHours: readCount(raw?.max_session_hours, raw?.maxSessionHours),
    reauthRequired,
  };
}

export function normalizeSupportUserSummary(
  raw: RawSupportUserSummary | null | undefined,
): SupportUserSummary | null {
  const id = readString(raw?.id);

  if (!raw || !id) {
    return null;
  }

  return {
    courseCount: readCount(raw.course_count, raw.courseCount),
    firstName: readString(raw.first_name, raw.firstName),
    id,
    isInstructor: readFlag(raw.is_instructor, raw.isInstructor),
    lastName: readString(raw.last_name, raw.lastName),
    matchedEmail: readFlag(raw.matched_email, raw.matchedEmail),
    matchedId: readFlag(raw.matched_id, raw.matchedId),
    preferredEmail: readString(raw.preferred_email, raw.preferredEmail),
  };
}

export function normalizeSupportUsers(
  raw: RawSupportConsoleSearchUsersResponse | null | undefined,
): SupportUserSummary[] {
  const listed = raw?.users;
  const users = Array.isArray(listed) ? listed : [];

  return users
    .map((user) => normalizeSupportUserSummary(user))
    .filter((user): user is SupportUserSummary => user !== null);
}

function normalizeInstructor(
  raw: RawSupportInstructor | null | undefined,
): SupportInstructor | null {
  const id = readString(raw?.id);

  if (!raw || !id) {
    return null;
  }

  return {
    email: readString(raw.email),
    firstName: readString(raw.first_name, raw.firstName),
    id,
    lastName: readString(raw.last_name, raw.lastName),
  };
}

export function normalizeSupportCourseMembership(
  raw: RawSupportCourseMembership | null | undefined,
): SupportCourseMembership | null {
  const courseId = readString(raw?.course_id, raw?.courseId);

  if (!raw || !courseId) {
    return null;
  }

  return {
    archived: readFlag(raw.archived),
    assistantCount: readCount(raw.assistant_count, raw.assistantCount),
    courseId,
    createdAt: readInstant(raw.created_utc_seconds, raw.createdUtcSeconds),
    endDate: readCalendarDate(raw.end_date ?? raw.endDate),
    exists: readFlag(raw.exists),
    inRoster: readFlag(raw.in_roster, raw.inRoster),
    instructor: normalizeInstructor(raw.instructor),
    isComplete: readFlag(raw.is_complete, raw.isComplete),
    isLive: readFlag(raw.is_live, raw.isLive),
    lastUpdatedAt: readInstant(raw.last_updated_utc_seconds, raw.lastUpdatedUtcSeconds),
    name: readString(raw.name),
    role: readCode(ROLES_BY_CODE, raw.role, 'unknown'),
    secondaryInstructorCount: readCount(
      raw.secondary_instructor_count,
      raw.secondaryInstructorCount,
    ),
    startDate: readCalendarDate(raw.start_date ?? raw.startDate),
    studentCount: readCount(raw.student_count, raw.studentCount),
    type: readCode(TYPES_BY_CODE, raw.type, 'unknown'),
  };
}

// Null when the answer names no user: the page then shows an error, never an
// empty profile.
export function normalizeSupportUserRecord(
  raw: RawSupportConsoleGetUserResponse | null | undefined,
): SupportUserRecord | null {
  const id = readString(raw?.user?.id);
  const user = raw?.user;

  if (!user || !id) {
    return null;
  }

  const listed = raw?.courses;
  const courses = Array.isArray(listed) ? listed : [];

  return {
    courses: courses
      .map((course) => normalizeSupportCourseMembership(course))
      .filter((course): course is SupportCourseMembership => course !== null),
    user: {
      firstName: readString(user.first_name, user.firstName),
      id,
      institute: readString(user.institute),
      instituteOfficialId: readString(user.institute_official_id, user.instituteOfficialId),
      isGoogle: readFlag(user.is_google, user.isGoogle),
      isInstructor: readFlag(user.is_instructor, user.isInstructor),
      lastLoginAt: readInstant(user.last_login_utc_seconds, user.lastLoginUtcSeconds),
      lastName: readString(user.last_name, user.lastName),
      preferredEmail: readString(user.preferred_email, user.preferredEmail),
      preferredUsername: readString(user.preferred_username, user.preferredUsername),
    },
  };
}

export function normalizeSupportRemoval(
  raw: RawSupportConsoleRemoveStudentResponse | null | undefined,
): SupportRemoval {
  return {
    removedFromArchivedList: readFlag(
      raw?.removed_from_archived_list,
      raw?.removedFromArchivedList,
    ),
    removedFromCourseList: readFlag(raw?.removed_from_course_list, raw?.removedFromCourseList),
    removedFromRoster: readFlag(raw?.removed_from_roster, raw?.removedFromRoster),
  };
}

// A person's name for a heading or a button: their name, else their email,
// else their ID.
export function supportDisplayName(person: {
  firstName: string;
  id: string;
  lastName: string;
  preferredEmail?: string;
}): string {
  const name = `${person.firstName} ${person.lastName}`.trim();
  return name || person.preferredEmail || person.id;
}
